import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { api, cachedGet } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import BookPage from "@/pages/BookPage";
import PrivatePage from "@/pages/PrivatePage";
import BookPrivateShow from "@/pages/BookPrivateShow";
import UpcomingShows from "@/pages/UpcomingShows";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import GatePage from "@/pages/GatePage";
import TicketPage from "@/pages/TicketPage";
import YatraPage from "@/pages/YatraPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import LeadFormPage from "@/pages/LeadFormPage";

export const WHATSAPP_NUMBER = "917013095204";
export const waLink = (msg = "Hi, I have a question about the show tickets.") =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

function WhatsAppFab() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname === "/gate") return null;
  return (
    <a href={waLink()} target="_blank" rel="noreferrer" aria-label="Chat with The Band Sumiran on WhatsApp"
      className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-black/40 hover:scale-110 transition-transform"
      data-testid="whatsapp-fab">
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  );
}

// Privacy-friendly visit ping on every public route change (no cookies)
function AnalyticsPing() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname === "/gate") return;
    api.post("/analytics/visit", { path: pathname }).catch(() => {});
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    cachedGet("/settings")
      .then(({ data: s }) => {
        if (s.seo_title) document.title = s.seo_title;
        if (s.seo_description) {
          document.querySelector('meta[name="description"]')?.setAttribute("content", s.seo_description);
        }
        if (s.pixel_head && !window.__pixelsInjected) {
          window.__pixelsInjected = true;
          const doc = new DOMParser().parseFromString(s.pixel_head, "text/html");
          doc.querySelectorAll("script").forEach((old) => {
            const el = document.createElement("script");
            if (old.src) el.src = old.src;
            else el.textContent = old.textContent;
            [...old.attributes].forEach((a) => { if (a.name !== "src") el.setAttribute(a.name, a.value); });
            document.head.appendChild(el);
          });
          doc.querySelectorAll("noscript").forEach((n) => {
            const holder = document.createElement("div");
            holder.style.display = "none";
            holder.innerHTML = n.innerHTML;
            document.body.appendChild(holder);
          });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<BookPage />} />
        <Route path="/book/:slug" element={<BookPage />} />
        <Route path="/private" element={<PrivatePage />} />
        <Route path="/bookprivateshow" element={<BookPrivateShow />} />
        <Route path="/upcomingshows" element={<UpcomingShows />} />
        <Route path="/yatra" element={<YatraPage />} />
        <Route path="/l/:slug" element={<LeadFormPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<LegalPage page="terms" />} />
        <Route path="/privacy" element={<LegalPage page="privacy" />} />
        <Route path="/refund" element={<LegalPage page="refund" />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/gate" element={<GatePage />} />
        <Route path="/ticket/:code" element={<TicketPage />} />
      </Routes>
      <WhatsAppFab />
      <AnalyticsPing />
      <Toaster theme="dark" position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
